/**
 * Feb 2026 · Admin Storefront Editor — new-field panel.
 *
 * Renders the storefront-specific editors (services, bios, FAQ,
 * testimonials, before/after gallery, SEO, completeness meter, live
 * preview) inside the existing `LandingForm`. Kept as a separate
 * component so the base form stays reviewable and existing tests
 * around the older fields don't move.
 *
 * Contract with the parent form:
 *   • `form`   — the current form state object.
 *   • `setField(k, v)` — mutator used by every input.
 *   • `initial` — the initial row from the API (used for preview URL).
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, Plus, ExternalLink, Star, Info, Image as ImageIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api from "@/lib/api";

// Kept in lockstep with backend `SERVICE_KEYS`.
const SERVICE_CATALOGUE = [
  { key: "water_testing",    label: "Water Testing" },
  { key: "tank_cleaning",    label: "Tank Cleaning" },
  { key: "filtration",       label: "Filtration" },
  { key: "biocleanse",       label: "BioCleanse" },
  { key: "uv_systems",       label: "UV Systems" },
  { key: "annual_servicing", label: "Annual Servicing" },
];

// Completeness meter — every "important" field contributes one point.
// A partner that scores 10/12 is "complete enough" to publish.
const COMPLETENESS_FIELDS = [
  { key: "business_name",       label: "Business name" },
  { key: "region_label",        label: "Region label" },
  { key: "phone_display",       label: "Phone" },
  { key: "email_display",       label: "Email" },
  { key: "franchisee_name",     label: "Franchisee name" },
  { key: "franchisee_photo_url", label: "Franchisee photo" },
  { key: "years_in_business",   label: "Years in business" },
  { key: "experience_bio",      label: "Experience bio" },
  { key: "why_choose_bio",      label: "Why choose bio" },
  { key: "service_suburbs",     label: "Service suburbs" },
  { key: "services_offered",    label: "Services offered" },
  { key: "testimonials",        label: "≥1 review" },
  { key: "faqs",                label: "≥1 FAQ" },
];

function scoreCompleteness(form) {
  const done = COMPLETENESS_FIELDS.filter((c) => {
    const v = form[c.key];
    if (Array.isArray(v)) return v.length > 0;
    if (typeof v === "object" && v !== null) return Object.values(v).some(Boolean);
    if (typeof v === "number") return v > 0;
    return !!(v && String(v).trim());
  });
  return { done: done.length, total: COMPLETENESS_FIELDS.length, items: done.map((c) => c.key) };
}

export default function StorefrontFieldsPanel({ form, setField, initial }) {
  // Jun 2026 — Google Place ID verification preview.
  const [placeCheck, setPlaceCheck] = useState(null); // {status: loading|ok|bad, ...}
  const verifyPlaceId = async () => {
    const pid = (form.google_place_id || "").trim();
    if (!pid) return;
    setPlaceCheck({ status: "loading" });
    try {
      const { data } = await api.get("/admin/pws-partner-landing/validate-place-id", { params: { place_id: pid } });
      setPlaceCheck(data.valid ? { status: "ok", ...data } : { status: "bad", error: data.error });
    } catch {
      setPlaceCheck({ status: "bad", error: "Could not check the Place ID — please try again" });
    }
  };

  const score = useMemo(() => scoreCompleteness(form), [form]);
  const pct   = Math.round((score.done / score.total) * 100);
  const previewUrl = initial?.slug ? `/pws-partner/${initial.slug}` : null;
  const isLive     = initial?.status === "published";

  // Small helpers used to mutate list-shaped fields immutably.
  const patchList = (key, index, patch) =>
    setField(key, (form[key] || []).map((it, i) => (i === index ? { ...it, ...patch } : it)));
  const removeAt  = (key, index) =>
    setField(key, (form[key] || []).filter((_, i) => i !== index));
  const appendItem = (key, item) =>
    setField(key, [...(form[key] || []), item]);

  return (
    <div className="sm:col-span-2 mt-2 pt-6 border-t border-slate-200 space-y-8" data-testid="storefront-editor-panel">
      {/* ── Completeness meter + preview button ─────────────── */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-50 to-white border border-slate-200 p-5 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[240px]">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider font-bold text-slate-600">
            <Info className="w-3.5 h-3.5" /> Storefront completeness
          </div>
          <div className="mt-2 flex items-center gap-3">
            <div className="flex-1 h-2 rounded-full bg-slate-200 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${pct >= 80 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-rose-500"}`}
                style={{ width: `${pct}%` }}
              />
            </div>
            <div className="text-sm font-bold text-slate-900 tabular-nums w-14" data-testid="storefront-editor-completeness">
              {score.done}/{score.total}
            </div>
          </div>
          <div className="mt-2 text-[11px] text-slate-500">
            {pct >= 80 ? "Ready to publish." : pct >= 50 ? "Nearly there — fill the highlighted sections." : "Needs more content before publishing."}
          </div>
        </div>
        {previewUrl && (
          <Link to={previewUrl} target="_blank" rel="noreferrer">
            <Button type="button" variant="outline" className="rounded-full border-slate-300 h-10 gap-2" data-testid="storefront-editor-preview-btn">
              {isLive ? "View live" : "Preview"} <ExternalLink className="w-4 h-4" />
            </Button>
          </Link>
        )}
      </div>

      {/* ── Meet-your-expert copy ─────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Years in business</label>
          <input
            type="number" min="0" max="100"
            data-testid="storefront-editor-years"
            value={form.years_in_business ?? ""}
            onChange={(e) => setField("years_in_business", e.target.value === "" ? null : Number(e.target.value))}
            className="mt-1 h-10 w-full rounded-md border border-slate-300 px-3 text-sm"
          />
        </div>
        <div>
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Google Place ID <span className="text-slate-400 font-normal">(shows live Google reviews)</span></label>
          <div className="mt-1 flex gap-2">
            <input
              data-testid="storefront-editor-place-id"
              value={form.google_place_id || ""}
              onChange={(e) => { setField("google_place_id", e.target.value); setPlaceCheck(null); }}
              className="h-10 w-full rounded-md border border-slate-300 px-3 text-sm font-mono"
              placeholder="ChIJ…"
            />
            <button
              type="button"
              data-testid="storefront-editor-place-id-verify"
              onClick={verifyPlaceId}
              disabled={!(form.google_place_id || "").trim() || placeCheck?.status === "loading"}
              className="h-10 px-4 rounded-md border border-slate-300 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40 whitespace-nowrap"
            >
              {placeCheck?.status === "loading" ? "Checking…" : "Verify"}
            </button>
          </div>
          {placeCheck?.status === "ok" && (
            <div data-testid="storefront-editor-place-id-result" className="mt-1.5 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-md px-2.5 py-1.5">
              ✓ Resolves to <b>{placeCheck.business_name}</b>{placeCheck.address ? ` — ${placeCheck.address}` : ""}
              {placeCheck.rating ? ` · ★ ${placeCheck.rating} (${placeCheck.total_review_count} reviews)` : ""}
            </div>
          )}
          {placeCheck?.status === "bad" && (
            <div data-testid="storefront-editor-place-id-error" className="mt-1.5 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-2.5 py-1.5">
              {placeCheck.error}
            </div>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Experience bio</label>
          <textarea
            data-testid="storefront-editor-experience-bio"
            value={form.experience_bio || ""}
            onChange={(e) => setField("experience_bio", e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            rows={5}
            maxLength={4000}
            placeholder="Shown under 'Meet Your Local Water Expert'. Write in first person — this is the franchisee's own introduction."
          />
        </div>
        <div className="sm:col-span-2">
          <label className="text-xs uppercase tracking-wider font-semibold text-slate-600">Why choose us</label>
          <textarea
            data-testid="storefront-editor-why-bio"
            value={form.why_choose_bio || ""}
            onChange={(e) => setField("why_choose_bio", e.target.value)}
            className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            rows={3}
            maxLength={2000}
            placeholder="What sets this partner apart? Local knowledge, response time, follow-up service…"
          />
        </div>
      </div>

      {/* ── Services offered ─────────────────────────────── */}
      <div>
        <div className="text-xs uppercase tracking-wider font-semibold text-slate-600 mb-2">Services offered</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="storefront-editor-services">
          {SERVICE_CATALOGUE.map((s) => {
            const on = !!(form.services_offered && form.services_offered[s.key]);
            return (
              <label
                key={s.key}
                className={`flex items-center gap-2 rounded-xl border p-3 text-sm cursor-pointer select-none transition ${on ? "border-cyan-400 bg-cyan-50" : "border-slate-200 bg-white hover:bg-slate-50"}`}
                data-testid={`storefront-editor-service-${s.key}`}
              >
                <input
                  type="checkbox"
                  checked={on}
                  onChange={(e) => setField("services_offered", {
                    ...(form.services_offered || {}),
                    [s.key]: e.target.checked,
                  })}
                />
                <span className={on ? "font-semibold text-cyan-900" : "text-slate-700"}>{s.label}</span>
              </label>
            );
          })}
        </div>
      </div>

      {/* ── Testimonials ─────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-600">Customer testimonials</div>
          <Button
            type="button" size="sm" variant="outline"
            className="ml-auto h-8 gap-1 text-xs"
            onClick={() => appendItem("testimonials", { id: `tst_${Date.now()}`, quote: "", author: "", suburb: "", rating: 5, photo_url: "" })}
            data-testid="storefront-editor-add-testimonial"
          >
            <Plus className="w-3 h-3" /> Add
          </Button>
        </div>
        <div className="space-y-3">
          {(form.testimonials || []).map((t, i) => (
            <div key={t.id} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid={`storefront-editor-testimonial-${i}`}>
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                <input
                  className="sm:col-span-3 h-9 rounded-md border border-slate-300 px-2 text-sm"
                  placeholder="Author name" value={t.author || ""}
                  onChange={(e) => patchList("testimonials", i, { author: e.target.value })}
                />
                <input
                  className="sm:col-span-2 h-9 rounded-md border border-slate-300 px-2 text-sm"
                  placeholder="Suburb" value={t.suburb || ""}
                  onChange={(e) => patchList("testimonials", i, { suburb: e.target.value })}
                />
                <div className="flex items-center gap-1">
                  {[1,2,3,4,5].map((r) => (
                    <button
                      key={r} type="button"
                      onClick={() => patchList("testimonials", i, { rating: r })}
                      className="p-0.5"
                      aria-label={`${r} stars`}
                    >
                      <Star className={`w-4 h-4 ${r <= (t.rating || 5) ? "text-amber-400 fill-amber-400" : "text-slate-300"}`} />
                    </button>
                  ))}
                </div>
              </div>
              <textarea
                className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
                rows={2}
                maxLength={1200}
                placeholder="What did the customer say?"
                value={t.quote || ""}
                onChange={(e) => patchList("testimonials", i, { quote: e.target.value })}
              />
              <div className="flex items-center gap-2">
                <input
                  className="flex-1 h-8 rounded-md border border-slate-300 px-2 text-xs"
                  placeholder="Customer photo URL (optional)"
                  value={t.photo_url || ""}
                  onChange={(e) => patchList("testimonials", i, { photo_url: e.target.value })}
                />
                <button
                  type="button"
                  onClick={() => removeAt("testimonials", i)}
                  className="p-1.5 rounded-md text-slate-500 hover:text-rose-700 hover:bg-rose-50"
                  data-testid={`storefront-editor-remove-testimonial-${i}`}
                  aria-label="Remove testimonial"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          ))}
          {(form.testimonials || []).length === 0 && (
            <div className="text-xs text-slate-500 italic">No testimonials yet. The Reviews section on the public page will hide entirely.</div>
          )}
        </div>
      </div>

      {/* ── FAQ ──────────────────────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-600">Frequently asked questions</div>
          <Button
            type="button" size="sm" variant="outline"
            className="ml-auto h-8 gap-1 text-xs"
            onClick={() => appendItem("faqs", { id: `faq_${Date.now()}`, question: "", answer: "" })}
            data-testid="storefront-editor-add-faq"
          >
            <Plus className="w-3 h-3" /> Add
          </Button>
        </div>
        <div className="space-y-3">
          {(form.faqs || []).map((f, i) => (
            <div key={f.id} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid={`storefront-editor-faq-${i}`}>
              <input
                className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm font-semibold"
                placeholder="Question" value={f.question || ""}
                onChange={(e) => patchList("faqs", i, { question: e.target.value })}
              />
              <textarea
                className="w-full rounded-md border border-slate-300 px-2 py-2 text-sm"
                rows={3} maxLength={2400}
                placeholder="Answer" value={f.answer || ""}
                onChange={(e) => patchList("faqs", i, { answer: e.target.value })}
              />
              <button
                type="button"
                onClick={() => removeAt("faqs", i)}
                className="text-xs text-slate-500 hover:text-rose-700 inline-flex items-center gap-1"
                data-testid={`storefront-editor-remove-faq-${i}`}
              >
                <Trash2 className="w-3 h-3" /> Remove
              </button>
            </div>
          ))}
          {(form.faqs || []).length === 0 && (
            <div className="text-xs text-slate-500 italic">No FAQs yet — the section will hide until at least one is added.</div>
          )}
        </div>
      </div>

      {/* ── Before/After gallery ─────────────────────────── */}
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-600">Before &amp; after pairs</div>
          <Button
            type="button" size="sm" variant="outline"
            className="ml-auto h-8 gap-1 text-xs"
            onClick={() => appendItem("gallery_before_after", { id: `ba_${Date.now()}`, before_url: "", after_url: "", caption: "" })}
            data-testid="storefront-editor-add-gallery"
          >
            <Plus className="w-3 h-3" /> Add pair
          </Button>
        </div>
        <div className="space-y-3">
          {(form.gallery_before_after || []).map((g, i) => (
            <div key={g.id} className="rounded-xl border border-slate-200 bg-white p-3 space-y-2" data-testid={`storefront-editor-gallery-${i}`}>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <GalleryImageInput
                  label="Before" url={g.before_url}
                  onChange={(v) => patchList("gallery_before_after", i, { before_url: v })}
                />
                <GalleryImageInput
                  label="After" url={g.after_url}
                  onChange={(v) => patchList("gallery_before_after", i, { after_url: v })}
                />
              </div>
              <input
                className="w-full h-9 rounded-md border border-slate-300 px-2 text-sm"
                placeholder="Caption (e.g. tank clean · Doonan 4562)" value={g.caption || ""}
                onChange={(e) => patchList("gallery_before_after", i, { caption: e.target.value })}
                maxLength={240}
              />
              <button
                type="button"
                onClick={() => removeAt("gallery_before_after", i)}
                className="text-xs text-slate-500 hover:text-rose-700 inline-flex items-center gap-1"
                data-testid={`storefront-editor-remove-gallery-${i}`}
              >
                <Trash2 className="w-3 h-3" /> Remove pair
              </button>
            </div>
          ))}
          {(form.gallery_before_after || []).length === 0 && (
            <div className="text-xs text-slate-500 italic">No pairs uploaded — the Before &amp; After section will hide entirely.</div>
          )}
        </div>
      </div>
    </div>
  );
}

function GalleryImageInput({ label, url, onChange }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">{label}</div>
      <div className="flex items-center gap-2">
        <input
          className="flex-1 h-9 rounded-md border border-slate-300 px-2 text-xs"
          placeholder={`${label} image URL`}
          value={url || ""}
          onChange={(e) => onChange(e.target.value)}
        />
        {url ? (
          <img src={url} alt={label} className="w-9 h-9 rounded-md object-cover border border-slate-200" />
        ) : (
          <div className="w-9 h-9 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
            <ImageIcon className="w-4 h-4" />
          </div>
        )}
      </div>
    </div>
  );
}
